import { useState } from 'react'
import { useApp } from '@/store/AppContext'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatCurrency } from '@/lib/pricing'
import {
  Lock,
  Unlock,
  Wallet,
  Banknote,
  Smartphone,
  CreditCard,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

export default function Cashier() {
  const { currentSession, openSession, closeSession, payments } = useApp()
  const { toast } = useToast()

  const [opening, setOpening] = useState(0)
  const [cash, setCash] = useState(0)
  const [pix, setPix] = useState(0)
  const [card, setCard] = useState(0)
  const [notes, setNotes] = useState('')

  const sessionPayments = currentSession?.openedAt
    ? payments.filter((p) => new Date(p.paidAt) >= currentSession.openedAt)
    : []

  const receivedCash = sessionPayments
    .filter((p) => p.method === 'Dinheiro')
    .reduce((a, b) => a + b.amount, 0)
  const expectedCash = receivedCash + (currentSession?.openingAmount || 0)
  const expectedPix = sessionPayments
    .filter((p) => p.method === 'Pix')
    .reduce((a, b) => a + b.amount, 0)
  const expectedCard = sessionPayments
    .filter((p) => p.method === 'Cartão')
    .reduce((a, b) => a + b.amount, 0)
  const totalReceived = receivedCash + expectedPix + expectedCard

  const handleOpen = () => {
    openSession(opening)
    toast({
      title: 'Caixa Aberto',
      description: `Sessão iniciada com ${formatCurrency(opening)}`,
    })
  }

  const handleClose = () => {
    closeSession(cash, pix, card, notes)
    toast({
      title: 'Caixa Fechado',
      description: 'Valores registrados com sucesso.',
    })
    setCash(0)
    setPix(0)
    setCard(0)
    setNotes('')
  }

  if (currentSession?.status === 'open') {
    const diff = cash + pix + card - (expectedCash + expectedPix + expectedCard)
    return (
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in-up">
        <h1 className="text-3xl font-bold">Gerenciamento de Caixa</h1>

        <div className="bg-emerald-50 text-emerald-800 px-4 py-3 rounded-lg border border-emerald-200 flex items-center gap-3 shadow-sm inline-flex">
          <Unlock className="h-5 w-5" />
          <span className="font-medium">
            Caixa aberto • iniciou às{' '}
            {currentSession.openedAt.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="shadow-elevation">
            <CardHeader className="bg-muted/30 pb-4 border-b">
              <CardTitle className="text-lg">Resumo da Sessão</CardTitle>
              <CardDescription>
                Valores recebidos durante este turno
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="flex justify-between items-center p-3 rounded-lg bg-background border">
                <div className="flex items-center gap-3">
                  <Banknote className="text-emerald-600" /> Dinheiro
                </div>
                <div className="font-bold text-lg">
                  {formatCurrency(receivedCash)}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background border">
                <div className="flex items-center gap-3">
                  <Smartphone className="text-teal-600" /> Pix
                </div>
                <div className="font-bold text-lg">
                  {formatCurrency(expectedPix)}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background border">
                <div className="flex items-center gap-3">
                  <CreditCard className="text-blue-600" /> Cartão
                </div>
                <div className="font-bold text-lg">
                  {formatCurrency(expectedCard)}
                </div>
              </div>
              <div className="flex justify-between items-center p-4 rounded-lg bg-primary text-primary-foreground mt-4 shadow-sm">
                <div className="font-bold uppercase tracking-wider text-sm">
                  Total Recebido
                </div>
                <div className="font-black text-2xl">
                  {formatCurrency(totalReceived)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-primary shadow-elevation">
            <CardHeader>
              <CardTitle className="text-xl">Fechamento do Turno</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Dinheiro na gaveta (inclui troco inicial)</Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      R$
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      value={cash || ''}
                      onChange={(e) => setCash(Number(e.target.value))}
                      className="h-12 text-lg pl-10 font-bold"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comprovantes Pix</Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      R$
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      value={pix || ''}
                      onChange={(e) => setPix(Number(e.target.value))}
                      className="h-12 text-lg pl-10 font-bold"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comprovantes Cartão (Maquininha)</Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      R$
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      value={card || ''}
                      onChange={(e) => setCard(Number(e.target.value))}
                      className="h-12 text-lg pl-10 font-bold"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted border flex justify-between items-center">
                  <span className="font-medium">Diferença apurada:</span>
                  <span
                    className={`font-bold text-xl ${diff < 0 ? 'text-destructive' : diff > 0 ? 'text-emerald-600' : ''}`}
                  >
                    {diff !== 0
                      ? (diff > 0 ? '+' : '') + formatCurrency(diff)
                      : 'Exato'}
                  </span>
                </div>

                <div className="space-y-2">
                  <Label>Observações (Opcional)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Justifique diferenças se houver..."
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleClose}
                variant="destructive"
                className="w-full h-14 text-lg shadow-md font-bold"
              >
                <Lock className="mr-2 h-5 w-5" /> Fechar Caixa e Concluir Turno
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto mt-10 animate-fade-in-up">
      <Card className="shadow-elevation">
        <CardHeader className="text-center pb-8 bg-muted/30">
          <div className="mx-auto w-20 h-20 bg-background shadow-sm rounded-full flex items-center justify-center mb-4 border">
            <Wallet className="h-10 w-10 text-muted-foreground" />
          </div>
          <CardTitle className="text-3xl">Caixa Fechado</CardTitle>
          <CardDescription className="text-base">
            Abra uma nova sessão para começar a operar e receber pagamentos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-8">
          <div className="space-y-3">
            <Label className="text-base">Fundo de Caixa Inicial (Troco)</Label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-lg">
                R$
              </div>
              <Input
                type="number"
                step="0.01"
                value={opening || ''}
                onChange={(e) => setOpening(Number(e.target.value))}
                className="h-16 text-2xl text-center font-black tracking-wider bg-muted/50 focus:bg-background"
                placeholder="0,00"
              />
            </div>
          </div>
          <Button
            onClick={handleOpen}
            className="w-full h-16 text-xl font-bold shadow-md"
          >
            Abrir Caixa
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
